import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserByUnitList } from '@app/models/profile.model';
import { CareConneRoomItem, UserByUnit } from '@app/models/user.model';
import { CareConneService } from '@app/services/care-conne/care-conne.service';
import { DialogService } from '@app/services/dialog.service';
import { RoomCategory } from '@app/shared/enum/care-conne.enum';
import { DataSelectors } from '@app/store/data/data.selectors';
import { CareConneSendersCreateByStudent, CleanCareConneSendersCreateByStudent } from '@app/store/record/record.classes';
import { RecordSelectors } from '@app/store/record/record.selectors';
import { Store } from '@ngxs/store';
import * as lodash from 'lodash';
import { ModalService } from '@app/services/modal.service';
import * as State from './slide-up-window-by-student.state';
import * as Utils from './slide-up-window-by-student.utils';
import * as Builder from './slide-up-window-by-student.builder';
import * as Filter from './slide-up-window-by-student.filter';
import * as Event from './slide-up-window-by-student.event';

@Component({
  selector: 'app-slide-up-window-by-student',
  templateUrl: './slide-up-window-by-student.component.html',
  styleUrls: ['./slide-up-window-by-student.component.scss']
})
export class SlideUpWindowByStudentComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    public store: Store,
    private cd: ChangeDetectorRef,
    private dialog: DialogService,
    private careConneService: CareConneService,
    private modalService: ModalService
  ) {
  }

  utils = Utils;
  state: State.SlideUpWindowByStudentState = { ...State.initialSlideUpWindowByStudentState };
  filterRoom = Filter;

  get selectedRoomsId() {
    return this.state?.form?.get('selectedRoomIds') as FormControl
  }

  get forwardMessage() {
    return this.state?.form?.get('message') as FormControl
  }

  get filterValue() {
    return Object.values(RoomCategory).filter(x => x != RoomCategory.SELFCHAT);
  }

  get rooms() {
    let _rooms = [...(this.state.filteredRoomData || [])];
    if (this.state.searchUser) {
      const keys = this.state.searchUser.split(/[ \u3000]+/).filter(x => x);
      _rooms = _rooms.filter(x => keys.some(key => x.name.toLowerCase().includes(key.toLocaleLowerCase())));
    }
    return _rooms;
  }

  get isMessageError() {
    return !this.state.isMessageOptional && this.state?.form?.dirty && this.forwardMessage.invalid;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.state.isInited = true;
      this.cd.detectChanges();
    }, 10);

  }
  ngOnDestroy(): void {
    this.careConneService.onDestroyModal.next();
    this.store.dispatch(new CleanCareConneSendersCreateByStudent);
  }

  ngOnInit(): void {
    this.state.filters = this.filterValue.map((x) => {
      return { name: x, isActive: true }
    })
    let config = this.store.selectSnapshot(RecordSelectors.currentCareConneSendersByStudent);
    let currentMessage = config[0]?.message || ''; // all student using same as message
    this.state.isMessageOptional = config[0]?.isMessageOptional || false;
    let _messageCondition = this.state.isMessageOptional ? [] : [Validators.required, Validators.minLength(1)];
    this.state.form = this.fb.group({
      // selectedRoomIds: [[], [Validators.required]],
      message: [(currentMessage || ''), _messageCondition],
    });
    this.route.data.subscribe((res) => {
      const data = res['roomsByStudent'] as Record<string, UserByUnit>;
      if (!data || Object.keys(data).length === 0) return;
      const flattedData = Object.values(data).flatMap((item) => item.roomDataByUnit);
      const groupedByUnit = lodash.groupBy(Object.values(flattedData), 'objcd');
      const userByUnitList = this.store.selectSnapshot(DataSelectors.userByUnitList);
      let mappedUnits: UserByUnitList[] = [];
      for (let key of Object.keys(groupedByUnit)) {
        const unitInfo = userByUnitList.find((item) => item.objcd === key);
        if (unitInfo) {
          mappedUnits.push(unitInfo);
        }
      }
      this.state.units = mappedUnits ?? [];
      this.onLoadUsers(this.state.units[0]);  //default select first unit
    })
  }

  resetSearch() {
    this.state.searchUser = '';
  }

  isSelected(room: CareConneRoomItem): boolean {
    const daicd = this.state.selectedUser?.daicd;
    if (!daicd) return false;

    return this.state.selectedRoomIdsByUser?.[daicd]?.roomIds?.includes(room.roomId) ?? false;
  }

  selectRoom(room: CareConneRoomItem) {
    const daicd = this.state.selectedUser?.daicd;
    if (!daicd) return;

    const current = this.state.selectedRoomIdsByUser?.[daicd]?.roomIds ?? [];
    const isExist = current.includes(room.roomId);
    const updatedRoomIds = isExist
      ? current.filter(id => id !== room.roomId)
      : [...current, room.roomId];

    this.state.selectedRoomIdsByUser = {
      ...this.state.selectedRoomIdsByUser,
      [daicd]: {
        roomIds: updatedRoomIds
      }
    };
    this.state.selectedRoomIdsByUser = Object.fromEntries(
        Object.entries(this.state.selectedRoomIdsByUser)
          .filter(([_, value]) => value.roomIds.length > 0)
    );
  }

  toNextPage() {
    if (this.isMsgInputBtnDisabled()) return;
    // change page animation
    this.state.currentPage = Math.min(this.state.currentPage + 1, 1);
  }

  toPrevPage() {
    this.state.currentPage = Math.max(this.state.currentPage - 1, 0);
  }

  isMsgInputBtnDisabled(): boolean {
    const noAnyRoomSelected = !Object.values(this.state.selectedRoomIdsByUser).some((item) => item.roomIds.length > 0);
    return  noAnyRoomSelected;
    // const allStudents = this.store.selectSnapshot(DataState.careConneRoomAllList);
    // return Object.keys(this.selectedRoomIdsByUser).length === 0 || 
    //        Object.keys(this.selectedRoomIdsByUser).length < Object.keys(allStudents).length;
  }

  isSendBtnDisabled(): boolean {
    return this.forwardMessage.invalid;
  }

  submit() {
    if (!this.state.isMessageOptional && this.forwardMessage.invalid) {
      this.state?.form?.markAsDirty();
      return;
    }
    // confirm value here
    const { message } = this.state?.form?.value;
    Builder.createRoomConfirm(this.store, this.state, message, this.dialog).afterClosed().subscribe(async (res: any) => {
      if (res) {
        const senders = Object.entries(this.state.selectedRoomIdsByUser)
          .filter(([_, v]) => v.roomIds.length > 0)
          .map(([daicd, v]) => ({ roomIds: v.roomIds, message: (message || ''), daicd }));
        this.store.dispatch(new CareConneSendersCreateByStudent(senders));
        await this.closeModal(false);
      }
    })
  }

  async closeModal(isReset: boolean) {
    await Utils.closeModal(this.state, this.store, isReset, this.route, this.router, this.modalService);
  }

  onLoadUsers(selectedUnit: UserByUnitList) {
    Event.onLoadUsers(this.state, this.store, selectedUnit);
    this.onLoadRooms(this.state.students[0]);  //default select first student
  }

  onLoadRooms(selectedUser: UserByUnit) {
    Event.onLoadRooms(this.state, selectedUser);
    Filter.filterByRoom(this.state, this.store);
  }
}
